import { mapActions, mapGetters, mapMutations } from 'vuex'

import SnackbarAction from '@/components/SnackbarAction/SnackbarAction.vue'
import CategoryContentReviewDialog from './CategoryContentReviewDialog.vue'
import { getCategoryContentField } from './categoryContentFields'

const MODE = 'paid'
const UPLOAD_CONCURRENCY = 4
const CATEGORY_CONTENT_LOAD_LIMIT = 5000
const PAGE_TRANSLATION_LOAD_LIMIT = 5000
const CATEGORY_CONTENT_STATUS_FIELDS = ['title', 'description']
const CATEGORY_CONTENT_STATUS_FILTERS = [
  { label: 'No status', value: 'no_status' },
  { label: 'Written', value: 'written' },
  { label: 'Uploaded', value: 'uploaded' }
]
const PAGE_TRANSLATION_STATUS_FILTERS = [
  { label: 'No status', value: 'no status' },
  { label: 'Translated', value: 'translated' },
  { label: 'Published', value: 'published' }
]
export default {
  name: 'CategoryContent',
  data () {
    return {
      search: '',
      viewMode: 'source',
      statusFilter: 'written',
      statusFilterTabs: CATEGORY_CONTENT_STATUS_FILTERS,
      translationStatusFilter: 'translated',
      translationStatusFilterTabs: PAGE_TRANSLATION_STATUS_FILTERS,
      rows: [],
      pageTranslationRows: [],
      visibleCategoryRows: [],
      visiblePageTranslationRows: [],
      selectedCategories: [],
      selectedPageTranslations: [],
      lastSelectedCategoryKey: null,
      lastSelectedPageTranslationKey: null,
      showSnackbar: false,
      previewDialog: {
        open: false,
        row: null
      },
      reviewDialog: {
        open: false,
        field: null,
        targets: []
      },
      totalServerCount: 0,
      totalPendingCount: 0,
      generationProgress: {
        status: null,
        total: 0,
        ready: 0,
        completedBatches: 0,
        batchCount: 0
      },
      generationRunId: null,
      generationSuccessMessage: '',
      generationRefreshTimer: null,
      tableOptions: {
        page: 1,
        itemsPerPage: 50,
        sortBy: [],
        sortDesc: [],
        groupBy: [],
        groupDesc: [],
        multiSort: false,
        mustSort: false
      },
      isLoading: {
        rows: false,
        trigger: false,
        regenerate: false,
        bulkTitleUpload: false,
        bulkDescriptionUpload: false,
        bulkTranslationUpload: false,
        pageTranslations: false,
        generateTranslations: false,
        uploads: {}
      },
      tableHeaders: [
        { text: '', value: 'select', width: '56px', sortable: false },
        { text: 'URL', value: 'url', width: '190px' },
        { text: 'Current Title', value: 'meta_title', width: '160px' },
        { text: 'Suggested Title', value: 'suggested_title', width: '170px' },
        { text: 'Current Description', value: 'category_description', width: '420px' },
        { text: 'Suggested Description', value: 'suggested_description', width: '460px' }
      ],
      translationTableHeaders: [
        { text: '', value: 'select', width: '56px', sortable: false },
        { text: 'Source URL', value: 'url', width: '190px' },
        { text: 'URL', value: 'translated_url', width: '190px' },
        { text: 'Suggested Title', value: 'suggested_title', width: '240px', sortable: false },
        { text: 'Suggested Description', value: 'suggested_description', width: '720px', sortable: false }
      ]
    }
  },
  computed: {
    ...mapGetters([
      'activeContentplan',
      'activeWorkspace',
      'websocketMessages'
    ]),
    workspaceId () {
      return parseInt(this.$route.params.workspaceId)
    },
    contentplanId () {
      return parseInt(this.$route.params.contentplanId)
    },
    contentplanName () {
      return this.activeContentplan?.name || `#${this.contentplanId}`
    },
    translationConfig () {
      return this.parseTranslationConfig(this.activeContentplan?.translation_config || this.activeContentplan?.translationConfig)
    },
    translationLanguages () {
      const languages = [
        ...(this.translationConfig?.translations || []).map(translation => this.normalizeValue(translation?.lang)),
        ...this.pageTranslationRows.map(row => this.normalizeValue(row?.language))
      ].filter(Boolean)

      return Array.from(new Set(languages))
    },
    viewTabs () {
      return [
        { label: 'Default', value: 'source' },
        ...this.translationLanguages.map(language => ({
          label: language.toUpperCase(),
          value: this.getTranslationViewValue(language),
          language
        }))
      ]
    },
    selectedTranslationLanguage () {
      if (!this.viewMode || !this.viewMode.startsWith('translation:')) return null
      return this.normalizeValue(this.viewMode.replace('translation:', ''))
    },
    isTranslationView () {
      return Boolean(this.selectedTranslationLanguage)
    },
    previewRow () {
      return this.previewDialog.row || {}
    },
    previewDialogKey () {
      const pageId = this.normalizeValue(this.previewRow.page_id) || this.normalizeValue(this.previewRow.id) || this.normalizeValue(this.previewRow.url) || 'empty'
      return `${pageId}:${this.normalizeValue(this.previewRow.language) || 'source'}`
    },
    previewCmsIds () {
      return this.getCmsIdDetails(this.previewRow)
    },
    isEligible () {
      return this.activeContentplan?.main_cms_integration === 'wordpress' && this.activeContentplan?.type === 'ecommerce'
    },
    remainingCategoryPageCredits () {
      return this.activeWorkspace?.remaining_category_page_credits || 0
    },
    selectedPageIds () {
      return this.selectedCategories.map(row => Number(this.getCategorySelectionKey(row))).filter(pageId => Number.isInteger(pageId) && pageId > 0)
    },
    selectedPendingCategories () {
      return this.selectedCategories.filter(row => this.isRowPending(row))
    },
    selectedGenerationCount () {
      return this.selectedPendingCategories.length
    },
    selectedUploadedCategories () {
      return this.selectedCategories.filter(row => this.isSourceCategoryReadyForTranslation(row))
    },
    selectedTranslationGenerationCount () {
      return this.selectedUploadedCategories.length
    },
    generationDisabledReason () {
      if (!this.isEligible) return 'Category content is only available for WordPress ecommerce content plans'
      if (!this.selectedCategories.length) return 'Select categories to generate'
      if (!this.selectedGenerationCount) return 'Selected categories already have suggestions'
      if (this.remainingCategoryPageCredits < this.selectedGenerationCount) return 'Not enough category page credits remaining'
      return ''
    },
    regenerationDisabledReason () {
      if (!this.isEligible) return 'Category content is only available for WordPress ecommerce content plans'
      if (!this.selectedPageIds.length) return 'Select categories to regenerate'
      if (this.remainingCategoryPageCredits < this.selectedPageIds.length) return 'Not enough category page credits remaining'
      return ''
    },
    translationGenerationDisabledReason () {
      if (!this.isEligible) return 'Category translations are only available for WordPress ecommerce content plans'
      if (!this.selectedCategories.length) return 'Select categories with a suggested title or description'
      if (!this.selectedTranslationGenerationCount) return 'Selected categories need a suggested title or description'
      return ''
    },
    reviewFieldConfig () {
      return getCategoryContentField(this.reviewDialog.field)
    },
    reviewFieldLabel () {
      return this.reviewFieldConfig?.uploadLabel || ''
    },
    isReviewUploadLoading () {
      const loadingKey = this.reviewFieldConfig?.loadingKey
      return loadingKey ? !!this.isLoading[loadingKey] : false
    },
    snackbarTitle () {
      if (this.isTranslationView) {
        if (!this.selectedPageTranslations.length) return ''
        return this.selectedPageTranslations.length === 1 ? '1 translation selected' : `${this.selectedPageTranslations.length} translations selected`
      }
      if (!this.selectedCategories.length) return ''
      return this.selectedCategories.length === 1 ? '1 category selected' : `${this.selectedCategories.length} categories selected`
    },
    snackbarActions () {
      if (this.isTranslationView) {
        return [
          {
            text: 'Upload selected',
            event: 'upload-translations-selected',
            loading: this.isLoading.bulkTranslationUpload,
            disabled: this.isTranslationUploadDisabled
          }
        ]
      }
      return [
        {
          text: 'Generate selected',
          event: 'generate-selected',
          loading: this.isLoading.trigger,
          disabled: !this.canTrigger
        },
        {
          text: 'Regenerate',
          event: 'regenerate-selected',
          loading: this.isLoading.regenerate,
          disabled: !this.canRegenerate
        },
        {
          text: 'Review title upload',
          event: 'upload-title-selected',
          loading: this.isLoading.bulkTitleUpload,
          disabled: this.isBulkUploadDisabled('title')
        },
        {
          text: 'Review text upload',
          event: 'upload-description-selected',
          loading: this.isLoading.bulkDescriptionUpload,
          disabled: this.isBulkUploadDisabled('description')
        },
        {
          text: 'Translate selected',
          event: 'translate-selected',
          loading: this.isLoading.generateTranslations,
          disabled: !!this.translationGenerationDisabledReason
        }
      ]
    },
    searchedRows () {
      if (!this.search) return this.rows
      const q = this.search.toLowerCase()
      return this.rows.filter(row => {
        return [
          row.url,
          row.meta_title,
          row.suggested_title,
          row.category_description,
          row.suggested_description
        ]
          .filter(Boolean)
          .some(value => String(value).toLowerCase().includes(q))
      })
    },
    filteredRows () {
      return this.searchedRows.filter(row => this.getCategoryPageStatus(row).value === this.statusFilter)
    },
    searchedPageTranslationRows () {
      if (!this.search) return this.pageTranslationRows
      const q = this.search.toLowerCase()
      return this.pageTranslationRows.filter(row => {
        return [
          row.url,
          row.translated_url,
          row.language,
          row.suggested_title,
          row.suggested_description
        ]
          .filter(Boolean)
          .some(value => String(value).toLowerCase().includes(q))
      })
    },
    filteredPageTranslationRows () {
      return this.searchedPageTranslationRows.filter(row => {
        if (this.selectedTranslationLanguage && this.normalizeValue(row.language) !== this.selectedTranslationLanguage) return false
        return (row.status || 'no status') === this.translationStatusFilter
      })
    },
    canTrigger () {
      return !this.generationDisabledReason && this.generationProgress.status !== 'running' && !this.isLoading.trigger && !this.isLoading.regenerate
    },
    canRegenerate () {
      return !this.regenerationDisabledReason && this.generationProgress.status !== 'running' && !this.isLoading.trigger && !this.isLoading.regenerate
    },
    generationProgressPercentage () {
      if (!this.generationProgress.total) return 0
      return Math.round((this.generationProgress.ready / this.generationProgress.total) * 100)
    },
    generationProgressTitle () {
      if (this.generationProgress.status === 'running' && this.generationProgress.batchCount) return `Generating category texts — ${this.generationProgress.completedBatches} of ${this.generationProgress.batchCount} backend batches complete`
      if (this.generationProgress.status === 'running') return 'Starting category text generation'
      if (this.generationProgress.status === 'error') return 'Category text generation stopped'
      if (this.generationProgress.ready === this.generationProgress.total) return 'Category texts are ready'
      return 'Category text generation completed with missing texts'
    },
    generationProgressType () {
      if (this.generationProgress.status === 'error') return 'error'
      if (this.generationProgress.status === 'running') return 'info'
      if (this.generationProgress.ready === this.generationProgress.total) return 'success'
      return 'warning'
    },
    areAllVisibleCategoryRowsSelected () {
      if (!this.visibleCategoryRows.length) return false
      return this.visibleCategoryRows.every(row => this.isCategorySelected(row))
    },
    areAllVisiblePageTranslationsSelected () {
      if (!this.visiblePageTranslationRows.length) return false
      return this.visiblePageTranslationRows.every(row => this.isPageTranslationSelected(row))
    },
    isTranslationUploadDisabled () {
      if (!this.selectedPageTranslations.length) return true
      if (this.isLoading.bulkTranslationUpload) return true
      return this.getSelectedPageTranslationUploadTargets().length === 0
    }
  },
  watch: {
    search () {
      this.tableOptions.page = 1
      this.lastSelectedCategoryKey = null
    },
    selectedCategories: {
      handler () {
        this.syncSnackbarVisibility()
      },
      deep: true
    },
    selectedPageTranslations: {
      handler () {
        this.syncSnackbarVisibility()
      },
      deep: true
    },
    websocketMessages: {
      handler (messages) {
        const trackerMessage = messages?.[messages.length - 1]?.messages?.[0]
        if (trackerMessage) this.handleCategoryGenerationTracker(trackerMessage)
      },
      deep: true
    },
    statusFilter () {
      this.tableOptions.page = 1
      this.lastSelectedCategoryKey = null
    },
    translationStatusFilter () {
      this.tableOptions.page = 1
      this.lastSelectedPageTranslationKey = null
    },
    viewMode () {
      this.tableOptions.page = 1
      this.lastSelectedCategoryKey = null
      this.lastSelectedPageTranslationKey = null
      this.syncSnackbarVisibility()
    },
    isEligible (newVal, oldVal) {
      if (newVal && !oldVal && !this.rows.length) {
        this.loadRows()
        this.loadPageTranslations()
      }
    }
  },
  async mounted () {
    if (this.isEligible) {
      await this.loadRows()
      await this.loadPageTranslations()
    }
  },
  beforeDestroy () {
    this.clearGenerationRefreshTimer()
  },
  methods: {
    ...mapActions([
      'pageGet',
      'categoryContentPost',
      'pageTranslationsGet',
      'pageTranslationsPost',
      'pageTranslationsPut',
      'uploadWordpressCategory',
      'loadWorkspaces'
    ]),
    ...mapMutations([
      'setSnackbar'
    ]),
    isRowPending (row) {
      if (!row?.suggested_title) return true
      if (!row?.suggested_description) return true
      return false
    },
    getMissingLabel (row) {
      const missing = []
      if (!row?.suggested_title) missing.push('title')
      if (!row?.suggested_description) missing.push('description')
      return missing.join(', ')
    },
    hasUploadedCategoryContent (row) {
      return Boolean(this.normalizeValue(row?.cg_origin))
    },
    isSourceCategoryReadyForTranslation (row) {
      const suggestedTitle = this.normalizeValue(row?.suggested_title)
      const suggestedDescription = this.normalizeValue(row?.suggested_description)
      return Boolean(suggestedTitle || suggestedDescription)
    },
    getCurrentCategoryValue (row, field) {
      const fieldConfig = getCategoryContentField(field)
      if (!fieldConfig) return null
      return this.normalizeValue(row?.[fieldConfig.currentField])
    },
    getCategoryFieldStatus (row, field) {
      const suggestedValue = this.getCategoryUploadValue(row, field)
      if (!suggestedValue) return { label: 'No status', color: 'grey' }

      const currentValue = this.getCurrentCategoryValue(row, field)
      if (this.hasUploadedCategoryContent(row) && currentValue === suggestedValue) {
        return { label: 'Uploaded', color: 'green' }
      }

      return { label: 'Written', color: 'primary' }
    },
    getCategoryPageStatus (row) {
      const fieldStatuses = CATEGORY_CONTENT_STATUS_FIELDS.map(field => this.getCategoryFieldStatus(row, field).label)
      if (fieldStatuses.some(status => status === 'Uploaded')) return { label: 'Uploaded', value: 'uploaded' }
      if (fieldStatuses.every(status => status === 'Written')) return { label: 'Written', value: 'written' }
      return { label: 'No status', value: 'no_status' }
    },
    getStatusFilterCount (statusFilter) {
      return this.searchedRows.filter(row => this.getCategoryPageStatus(row).value === statusFilter).length
    },
    getTranslationStatusFilterCount (statusFilter) {
      return this.searchedPageTranslationRows.filter(row => {
        if (this.selectedTranslationLanguage && this.normalizeValue(row.language) !== this.selectedTranslationLanguage) return false
        return (row.status || 'no status') === statusFilter
      }).length
    },
    getValueOrDash (value) {
      if (!value) return '-'
      return value
    },
    getCategorySelectionKey (row) {
      return this.normalizeValue(row?.page_id) || this.normalizeValue(row?.id) || this.normalizeValue(row?.url) || null
    },
    getPageTranslationSelectionKey (row) {
      const pageId = this.normalizeValue(row?.page_id)
      const language = this.normalizeValue(row?.language)
      if (pageId && language) return `${pageId}:${language}`
      return this.normalizeValue(row?.translated_url) || null
    },
    toggleSelectCategory (row, event = null) {
      const selectionKey = this.getCategorySelectionKey(row)
      if (!selectionKey) return
      if (event?.shiftKey && this.lastSelectedCategoryKey) {
        this.selectCategoryRange(this.lastSelectedCategoryKey, selectionKey)
        this.lastSelectedCategoryKey = selectionKey
        return
      }

      const index = this.selectedCategories.findIndex(category => this.getCategorySelectionKey(category) === selectionKey)
      if (index === -1) this.selectedCategories.push(row)
      else this.selectedCategories.splice(index, 1)
      this.lastSelectedCategoryKey = selectionKey
    },
    selectCategoryRange (startKey, endKey) {
      const startIndex = this.filteredRows.findIndex(row => this.getCategorySelectionKey(row) === startKey)
      const endIndex = this.filteredRows.findIndex(row => this.getCategorySelectionKey(row) === endKey)
      if (startIndex === -1 || endIndex === -1) return

      const [from, to] = startIndex < endIndex ? [startIndex, endIndex] : [endIndex, startIndex]
      const selectedKeys = new Set(this.selectedCategories.map(row => this.getCategorySelectionKey(row)).filter(Boolean))
      const nextSelectedCategories = [...this.selectedCategories]

      this.filteredRows.slice(from, to + 1).forEach(row => {
        const rowKey = this.getCategorySelectionKey(row)
        if (!rowKey || selectedKeys.has(rowKey)) return
        selectedKeys.add(rowKey)
        nextSelectedCategories.push(row)
      })

      this.selectedCategories = nextSelectedCategories
    },
    isCategorySelected (row) {
      const selectionKey = this.getCategorySelectionKey(row)
      if (!selectionKey) return false
      return this.selectedCategories.some(category => this.getCategorySelectionKey(category) === selectionKey)
    },
    clearSelectedCategories () {
      this.selectedCategories = []
      this.lastSelectedCategoryKey = null
    },
    setVisibleCategoryRows (rows) {
      this.visibleCategoryRows = rows
    },
    toggleSelectAllVisibleCategoryRows () {
      if (!this.visibleCategoryRows.length) return
      if (this.areAllVisibleCategoryRowsSelected) {
        const visibleKeys = new Set(this.visibleCategoryRows.map(row => this.getCategorySelectionKey(row)).filter(Boolean))
        this.selectedCategories = this.selectedCategories.filter(category => !visibleKeys.has(this.getCategorySelectionKey(category)))
        return
      }

      const nextSelectedCategories = [...this.selectedCategories]
      this.visibleCategoryRows.forEach(row => {
        if (!this.isCategorySelected(row)) nextSelectedCategories.push(row)
      })
      this.selectedCategories = nextSelectedCategories
    },
    toggleSelectPageTranslation (row, event = null) {
      const selectionKey = this.getPageTranslationSelectionKey(row)
      if (!selectionKey) return
      if (event?.shiftKey && this.lastSelectedPageTranslationKey) {
        this.selectPageTranslationRange(this.lastSelectedPageTranslationKey, selectionKey)
        this.lastSelectedPageTranslationKey = selectionKey
        return
      }

      const index = this.selectedPageTranslations.findIndex(translation => this.getPageTranslationSelectionKey(translation) === selectionKey)
      if (index === -1) this.selectedPageTranslations.push(row)
      else this.selectedPageTranslations.splice(index, 1)
      this.lastSelectedPageTranslationKey = selectionKey
    },
    selectPageTranslationRange (startKey, endKey) {
      const startIndex = this.filteredPageTranslationRows.findIndex(row => this.getPageTranslationSelectionKey(row) === startKey)
      const endIndex = this.filteredPageTranslationRows.findIndex(row => this.getPageTranslationSelectionKey(row) === endKey)
      if (startIndex === -1 || endIndex === -1) return

      const [from, to] = startIndex < endIndex ? [startIndex, endIndex] : [endIndex, startIndex]
      const selectedKeys = new Set(this.selectedPageTranslations.map(row => this.getPageTranslationSelectionKey(row)).filter(Boolean))
      const nextSelectedTranslations = [...this.selectedPageTranslations]

      this.filteredPageTranslationRows.slice(from, to + 1).forEach(row => {
        const rowKey = this.getPageTranslationSelectionKey(row)
        if (!rowKey || selectedKeys.has(rowKey)) return
        selectedKeys.add(rowKey)
        nextSelectedTranslations.push(row)
      })

      this.selectedPageTranslations = nextSelectedTranslations
    },
    isPageTranslationSelected (row) {
      const selectionKey = this.getPageTranslationSelectionKey(row)
      if (!selectionKey) return false
      return this.selectedPageTranslations.some(translation => this.getPageTranslationSelectionKey(translation) === selectionKey)
    },
    setVisiblePageTranslationRows (rows) {
      this.visiblePageTranslationRows = rows
    },
    toggleSelectAllVisiblePageTranslations () {
      if (!this.visiblePageTranslationRows.length) return
      if (this.areAllVisiblePageTranslationsSelected) {
        const visibleKeys = new Set(this.visiblePageTranslationRows.map(row => this.getPageTranslationSelectionKey(row)).filter(Boolean))
        this.selectedPageTranslations = this.selectedPageTranslations.filter(translation => !visibleKeys.has(this.getPageTranslationSelectionKey(translation)))
        return
      }

      const nextSelectedTranslations = [...this.selectedPageTranslations]
      this.visiblePageTranslationRows.forEach(row => {
        if (!this.isPageTranslationSelected(row)) nextSelectedTranslations.push(row)
      })
      this.selectedPageTranslations = nextSelectedTranslations
    },
    clearSelectedPageTranslations () {
      this.selectedPageTranslations = []
      this.lastSelectedPageTranslationKey = null
    },
    syncSnackbarVisibility () {
      this.showSnackbar = this.isTranslationView ? this.selectedPageTranslations.length > 0 : this.selectedCategories.length > 0
    },
    syncSelectedCategories () {
      if (!this.selectedCategories.length) return
      const selectedKeys = new Set(this.selectedCategories.map(row => this.getCategorySelectionKey(row)).filter(Boolean))
      this.selectedCategories = this.rows.filter(row => selectedKeys.has(this.getCategorySelectionKey(row)))
    },
    syncSelectedPageTranslations () {
      if (!this.selectedPageTranslations.length) return
      const selectedKeys = new Set(this.selectedPageTranslations.map(row => this.getPageTranslationSelectionKey(row)).filter(Boolean))
      this.selectedPageTranslations = this.pageTranslationRows.filter(row => selectedKeys.has(this.getPageTranslationSelectionKey(row)))
    },
    getUploadKey (row, field) {
      const rowId = this.normalizeValue(row?.page_id) || this.normalizeValue(row?.id) || this.normalizeValue(row?.url) || 'unknown'
      return `${field}:${rowId}`
    },
    getWordpressCategoryId (row) {
      const wordpressId = this.normalizeValue(row?.wordpress_id)
      if (!wordpressId) return null
      const parsedId = Number(wordpressId)
      if (!Number.isInteger(parsedId) || parsedId <= 0) return null
      return parsedId
    },
    getCategoryUploadValue (row, field) {
      const fieldConfig = getCategoryContentField(field)
      if (!fieldConfig) return null
      return this.normalizeValue(row?.[fieldConfig.suggestedField])
    },
    buildCategoryUploadPayload (row, field) {
      const fieldConfig = getCategoryContentField(field)
      if (!fieldConfig) return null
      const categoryId = this.getWordpressCategoryId(row)
      if (!categoryId) return null

      const uploadValue = this.getCategoryUploadValue(row, field)
      if (!uploadValue) return null

      const payload = {
        contentplanId: this.contentplanId,
        categoryId
      }
      payload[fieldConfig.payloadField] = uploadValue
      return payload
    },
    buildPageTranslationUploadPayload (row) {
      const categoryId = this.getWordpressCategoryId(row)
      const language = this.normalizeValue(row?.language)
      const metaTitle = this.normalizeValue(row?.suggested_title)
      const description = this.normalizeValue(row?.suggested_description)
      if (!categoryId || !language || !metaTitle || !description) return null
      return {
        contentplanId: this.contentplanId,
        categoryId,
        language,
        metaTitle,
        description
      }
    },
    getSelectedUploadTargets (field) {
      return this.selectedCategories.reduce((targets, row) => {
        const payload = this.buildCategoryUploadPayload(row, field)
        if (!payload) return targets
        targets.push({ row, payload })
        return targets
      }, [])
    },
    isBulkUploadDisabled (field) {
      const fieldConfig = getCategoryContentField(field)
      if (!fieldConfig) return true
      if (!this.selectedCategories.length) return true
      if (this.isLoading[fieldConfig.loadingKey]) return true
      return this.getSelectedUploadTargets(field).length === 0
    },
    getSelectedPageTranslationUploadTargets () {
      return this.selectedPageTranslations.reduce((targets, row) => {
        const payload = this.buildPageTranslationUploadPayload(row)
        if (!payload) return targets
        targets.push({ row, payload })
        return targets
      }, [])
    },
    openBulkUploadReview (field) {
      const uploadTargets = this.getSelectedUploadTargets(field)
      const fieldLabel = getCategoryContentField(field)?.uploadLabel || field
      if (!uploadTargets.length) {
        this.setSnackbar({ open: true, text: `No selected categories have a WordPress id and suggested ${fieldLabel} to upload`, type: 'info' })
        return
      }
      this.openReviewDialog(field, uploadTargets)
    },
    openReviewDialog (field, uploadTargets) {
      this.reviewDialog.field = field
      this.reviewDialog.targets = uploadTargets.map(target => ({
        ...target,
        key: this.getCategorySelectionKey(target.row),
        url: target.row.url,
        currentValue: this.getCurrentCategoryValue(target.row, field),
        suggestedValue: this.getCategoryUploadValue(target.row, field)
      }))
      this.reviewDialog.open = true
    },
    closeReviewDialog () {
      this.reviewDialog.open = false
      this.reviewDialog.field = null
      this.reviewDialog.targets = []
    },
    async confirmReviewUpload () {
      const field = this.reviewDialog.field
      const targets = [...this.reviewDialog.targets]
      if (targets.length === 1) {
        await this.uploadReviewedCategorySuggestion(targets[0].row, field, targets[0].payload)
      } else {
        await this.handleBulkUpload(field, targets)
      }
      this.closeReviewDialog()
    },
    async uploadReviewedCategorySuggestion (row, field, payload) {
      const uploadKey = this.getUploadKey(row, field)
      if (this.isLoading.uploads[uploadKey]) return
      this.$set(this.isLoading.uploads, uploadKey, true)
      try {
        await this.uploadWordpressCategory(payload)
        this.setSnackbar({ open: true, text: `Uploaded suggested ${field} to WordPress category`, type: 'success' })
        await this.loadRows()
      } catch (err) {
        this.setSnackbar({ open: true, text: this.getErrorMessage(err, `Failed to upload suggested ${field}`), type: 'error' })
      } finally {
        this.$delete(this.isLoading.uploads, uploadKey)
      }
    },
    async handleBulkUpload (field, uploadTargets = this.getSelectedUploadTargets(field)) {
      const skippedCount = this.selectedCategories.length - uploadTargets.length
      const fieldConfig = getCategoryContentField(field)
      const fieldLabel = fieldConfig?.uploadLabel || field
      if (!uploadTargets.length) {
        this.setSnackbar({ open: true, text: `No selected categories have a WordPress id and suggested ${fieldLabel} to upload`, type: 'info' })
        return
      }

      const loadingKey = fieldConfig.loadingKey
      this.isLoading[loadingKey] = true

      try {
        const results = await this.runLimitedQueue(uploadTargets, async target => await this.uploadWordpressCategory(target.payload), UPLOAD_CONCURRENCY)
        const failedKeys = new Set()
        let successCount = 0

        results.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            successCount++
            return
          }
          failedKeys.add(this.getCategorySelectionKey(uploadTargets[index].row))
        })

        if (successCount > 0) await this.loadRows()

        if (!failedKeys.size) this.clearSelectedCategories()
        else this.selectedCategories = this.rows.filter(row => failedKeys.has(this.getCategorySelectionKey(row)))

        const failureCount = uploadTargets.length - successCount
        if (failureCount === 0) {
          const skippedSuffix = skippedCount ? ` Skipped ${skippedCount} category${skippedCount === 1 ? ' because it was' : 'ies because they were'} missing a WordPress id or suggested ${fieldLabel}.` : ''
          this.setSnackbar({
            open: true,
            text: `Uploaded ${fieldLabel} for ${successCount} ${successCount === 1 ? 'category' : 'categories'}.${skippedSuffix}`,
            type: 'success'
          })
          return
        }

        if (successCount > 0) {
          const skippedSuffix = skippedCount ? ` ${skippedCount} selected category${skippedCount === 1 ? ' was' : 'ies were'} skipped before upload.` : ''
          this.setSnackbar({
            open: true,
            text: `Uploaded ${fieldLabel} for ${successCount} ${successCount === 1 ? 'category' : 'categories'}. ${failureCount} failed.${skippedSuffix}`,
            type: 'warning'
          })
          return
        }

        this.setSnackbar({
          open: true,
          text: `Failed to upload ${fieldLabel} for the selected categories.`,
          type: 'error'
        })
      } catch (err) {
        this.setSnackbar({ open: true, text: this.getErrorMessage(err, `Failed to upload ${fieldLabel} for the selected categories.`), type: 'error' })
      } finally {
        this.isLoading[loadingKey] = false
      }
    },
    async handleBulkTranslationUpload () {
      const uploadTargets = this.getSelectedPageTranslationUploadTargets()
      const skippedCount = this.selectedPageTranslations.length - uploadTargets.length
      if (!uploadTargets.length) {
        this.setSnackbar({ open: true, text: 'No selected page translations have a language, WordPress id, title, and text to upload', type: 'info' })
        return
      }

      this.isLoading.bulkTranslationUpload = true

      try {
        const results = await this.runLimitedQueue(uploadTargets, async target => await this.uploadWordpressCategory(target.payload), UPLOAD_CONCURRENCY)
        const failedKeys = new Set()
        let successCount = 0

        results.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            successCount++
            return
          }
          failedKeys.add(this.getPageTranslationSelectionKey(uploadTargets[index].row))
        })

        if (successCount > 0) await this.loadPageTranslations()

        if (!failedKeys.size) this.clearSelectedPageTranslations()
        else this.selectedPageTranslations = this.pageTranslationRows.filter(row => failedKeys.has(this.getPageTranslationSelectionKey(row)))

        const failureCount = uploadTargets.length - successCount
        const skippedSuffix = skippedCount ? ` Skipped ${skippedCount} selected translation${skippedCount === 1 ? '' : 's'} because required fields were missing.` : ''
        this.setSnackbar({
          open: true,
          text: failureCount
            ? `Uploaded ${successCount} translation${successCount === 1 ? '' : 's'}. ${failureCount} failed.${skippedSuffix}`
            : `Uploaded ${successCount} translation${successCount === 1 ? '' : 's'}.${skippedSuffix}`,
          type: failureCount ? 'warning' : 'success'
        })
      } catch (err) {
        this.setSnackbar({ open: true, text: this.getErrorMessage(err, 'Failed to upload translated category content'), type: 'error' })
      } finally {
        this.isLoading.bulkTranslationUpload = false
      }
    },
    async runLimitedQueue (items, worker, concurrency) {
      const results = new Array(items.length)
      let nextIndex = 0
      const workers = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
        while (nextIndex < items.length) {
          const currentIndex = nextIndex
          nextIndex++
          try {
            results[currentIndex] = { status: 'fulfilled', value: await worker(items[currentIndex]) }
          } catch (reason) {
            results[currentIndex] = { status: 'rejected', reason }
          }
        }
      })
      await Promise.all(workers)
      return results
    },
    handleSnackbarAction (event) {
      if (event === 'upload-title-selected') {
        this.openBulkUploadReview('title')
        return
      }
      if (event === 'upload-description-selected') {
        this.openBulkUploadReview('description')
        return
      }
      if (event === 'translate-selected') {
        this.triggerPageTranslationsPost()
        return
      }
      if (event === 'upload-translations-selected') {
        this.handleBulkTranslationUpload()
        return
      }
      if (event === 'regenerate-selected') {
        this.triggerCategoryContentRegeneration()
        return
      }
      if (event === 'generate-selected') this.triggerCategoryContentPost()
    },
    handleSnackbarCancel () {
      if (this.isTranslationView) {
        this.clearSelectedPageTranslations()
        return
      }
      this.clearSelectedCategories()
    },
    getTranslationViewValue (language) {
      return `translation:${language}`
    },
    parseTranslationConfig (value) {
      if (!value) return { original: {}, translations: [] }
      if (typeof value === 'object') {
        return {
          original: value.original || {},
          translations: Array.isArray(value.translations) ? value.translations : []
        }
      }
      try {
        const parsed = JSON.parse(value)
        return {
          original: parsed?.original || {},
          translations: Array.isArray(parsed?.translations) ? parsed.translations : []
        }
      } catch (err) {
        return { original: {}, translations: [] }
      }
    },
    normalizeValue (value) {
      if (value === undefined || value === null) return null
      const normalized = String(value).trim()
      if (!normalized) return null
      return normalized
    },
    truncateText (value, maxLength = 120) {
      const normalized = this.normalizeValue(value)
      if (!normalized) return '-'
      if (normalized.length <= maxLength) return normalized
      return `${normalized.slice(0, maxLength).trim()}...`
    },
    getCmsIdDetails (row = {}) {
      const cmsFields = [
        { field: 'wordpress_id', label: 'WordPress' }
      ]

      return cmsFields.reduce((details, cmsField) => {
        const cmsId = this.normalizeValue(row[cmsField.field])
        if (!cmsId) return details
        details.push({ ...cmsField, id: cmsId })
        return details
      }, [])
    },
    openPreview (row) {
      this.previewDialog.row = { ...row }
      this.previewDialog.open = true
    },
    stopRowClick (event) {
      if (event) event.stopPropagation()
    },
    closePreview () {
      this.previewDialog.open = false
      this.previewDialog.row = null
    },
    async refreshData () {
      await Promise.all([
        this.loadRows(),
        this.loadPageTranslations()
      ])
    },
    async loadRows () {
      if (!this.contentplanId || !this.isEligible) return
      this.isLoading.rows = true
      try {
        const baseFilters = [
          { field: 'contentplanId', op: 'eq', value: this.contentplanId },
          { field: 'type', op: 'eq', value: 'category' },
          { field: 'excluded', op: 'eq', value: 0 },
          { field: 'statusCode', op: 'eq', value: 200 },
          { field: 'analyzedAt', op: 'is_null', value: false },
          { field: 'wordpressId', op: 'is_null', value: false }
        ]
        const listParams = {
          contentplanId: this.contentplanId,
          skipCommit: true,
          sortColumn: 'url',
          sortDirection: 'asc',
          limit: CATEGORY_CONTENT_LOAD_LIMIT,
          page: 1,
          filters: baseFilters
        }
        const pendingParams = {
          contentplanId: this.contentplanId,
          skipCommit: true,
          sortColumn: 'url',
          sortDirection: 'asc',
          limit: 1,
          page: 1,
          filters: baseFilters,
          or: [
            { field: 'suggestedTitle', op: 'is_null', value: true },
            { field: 'suggestedDescription', op: 'is_null', value: true }
          ]
        }
        const [firstListRes, pendingRes] = await Promise.all([
          this.pageGet(listParams),
          this.pageGet(pendingParams)
        ])
        const totalCount = firstListRes?.meta?.totalCount ?? 0
        const rows = firstListRes?.items || []
        const pageCount = Math.ceil(totalCount / CATEGORY_CONTENT_LOAD_LIMIT)

        for (let page = 2; page <= pageCount; page++) {
          const res = await this.pageGet({
            ...listParams,
            page
          })
          rows.push(...(res?.items || []))
        }

        this.rows = rows
        this.totalServerCount = totalCount
        this.totalPendingCount = pendingRes?.meta?.totalCount ?? 0
        this.syncSelectedCategories()
      } catch (err) {
        this.setSnackbar({ open: true, text: this.getErrorMessage(err, 'Failed to load category content rows'), type: 'error' })
      } finally {
        this.isLoading.rows = false
      }
    },
    async loadPageTranslations () {
      if (!this.contentplanId || !this.isEligible) return
      this.isLoading.pageTranslations = true
      try {
        const res = await this.pageTranslationsGet({
          contentplanId: this.contentplanId,
          type: 'category',
          limit: PAGE_TRANSLATION_LOAD_LIMIT,
          offset: 0
        })
        this.pageTranslationRows = (res?.items || []).map(row => ({
          ...row,
          translation_key: this.getPageTranslationSelectionKey(row)
        }))
        this.syncSelectedPageTranslations()
      } catch (err) {
        this.setSnackbar({ open: true, text: this.getErrorMessage(err, 'Failed to load page translations'), type: 'error' })
      } finally {
        this.isLoading.pageTranslations = false
      }
    },
    async triggerCategoryContentPost () {
      if (!this.canTrigger) {
        this.setSnackbar({ open: true, text: this.generationDisabledReason || 'No pending rows to process', type: 'info' })
        return
      }
      await this.runCategoryContentGeneration({
        pageIds: this.selectedPendingCategories.map(row => Number(this.getCategorySelectionKey(row))),
        loadingKey: 'trigger',
        successMessage: 'Category content generation finished'
      })
    },
    async triggerCategoryContentRegeneration () {
      if (!this.canRegenerate) {
        this.setSnackbar({ open: true, text: this.regenerationDisabledReason || 'No categories selected to regenerate', type: 'info' })
        return
      }
      await this.runCategoryContentGeneration({
        pageIds: this.selectedPageIds,
        forceRegenerate: true,
        loadingKey: 'regenerate',
        successMessage: 'Category content regenerated'
      })
    },
    async runCategoryContentGeneration ({ pageIds, forceRegenerate = false, loadingKey, successMessage }) {
      this.isLoading[loadingKey] = true
      const runId = this.createGenerationRunId()
      this.generationRunId = runId
      this.generationSuccessMessage = successMessage
      this.generationProgress = {
        status: 'running',
        total: pageIds.length,
        ready: 0,
        completedBatches: 0,
        batchCount: 0
      }
      try {
        const response = await this.categoryContentPost({
          contentplanId: this.contentplanId,
          mode: MODE,
          pageIds,
          forceRegenerate,
          runId
        })
        const batchCount = Number(response?.batchCount)
        if (response?.runId !== runId || Number(response?.pagesQueued) !== pageIds.length || !Number.isInteger(batchCount)) throw new Error('Category content generation was not queued correctly')
        this.generationProgress.batchCount = Math.max(this.generationProgress.batchCount, batchCount)
        if (this.generationProgress.status === 'running') {
          this.setSnackbar({ open: true, text: `Started generation for ${pageIds.length} category texts`, type: 'info' })
        }
      } catch (err) {
        this.generationProgress.status = 'error'
        this.setSnackbar({ open: true, text: this.getErrorMessage(err, 'Failed to start category content generation'), type: 'error' })
      } finally {
        this.isLoading[loadingKey] = false
      }
    },
    createGenerationRunId () {
      return `${this.contentplanId}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
    },
    async handleCategoryGenerationTracker (message) {
      if (message?.stepName !== 'suggestNewTitles') return
      if (Number(message.contentplanId) !== this.contentplanId) return
      if (!message.runId || message.runId !== this.generationRunId) return

      const readyPages = Number(message.readyPages)
      const totalPages = Number(message.totalPages)
      const completedBatches = Number(message.completedBatches)
      const batchCount = Number(message.batchCount)
      const readyChanged = Number.isFinite(readyPages) && readyPages > this.generationProgress.ready
      if (Number.isFinite(readyPages)) this.generationProgress.ready = Math.max(this.generationProgress.ready, readyPages)
      if (Number.isFinite(totalPages) && totalPages > 0) this.generationProgress.total = totalPages
      if (Number.isFinite(completedBatches)) this.generationProgress.completedBatches = Math.max(this.generationProgress.completedBatches, completedBatches)
      if (Number.isFinite(batchCount)) this.generationProgress.batchCount = Math.max(this.generationProgress.batchCount, batchCount)

      if (message.status === 'inProgress') {
        this.generationProgress.status = 'running'
        if (readyChanged) this.scheduleGenerationRowsRefresh()
        return
      }

      this.clearGenerationRefreshTimer()
      this.generationProgress.status = message.status === 'done' ? 'done' : 'error'
      const allTextsReady = this.generationProgress.ready === this.generationProgress.total
      if (message.status === 'done') {
        this.setSnackbar({
          open: true,
          text: `${this.generationSuccessMessage}: ${this.generationProgress.ready} of ${this.generationProgress.total} texts ready`,
          type: allTextsReady ? 'success' : 'warning'
        })
      } else {
        this.setSnackbar({
          open: true,
          text: `${message.message || 'Category content generation failed'}. ${this.generationProgress.ready} of ${this.generationProgress.total} texts are ready.`,
          type: 'error'
        })
      }
      await Promise.all([this.loadRows(), this.loadWorkspaces()])
    },
    scheduleGenerationRowsRefresh () {
      this.clearGenerationRefreshTimer()
      this.generationRefreshTimer = setTimeout(() => {
        this.generationRefreshTimer = null
        this.loadRows()
      }, 1000)
    },
    clearGenerationRefreshTimer () {
      if (!this.generationRefreshTimer) return
      clearTimeout(this.generationRefreshTimer)
      this.generationRefreshTimer = null
    },
    async triggerPageTranslationsPost () {
      if (this.translationGenerationDisabledReason) {
        this.setSnackbar({ open: true, text: this.translationGenerationDisabledReason, type: 'info' })
        return
      }
      this.isLoading.generateTranslations = true
      try {
        await this.pageTranslationsPost({
          contentplanId: this.contentplanId,
          type: 'category',
          pageIds: this.selectedUploadedCategories.map(row => Number(this.getCategorySelectionKey(row)))
        })
        this.setSnackbar({ open: true, text: 'Category translations generated', type: 'success' })
        const language = this.translationLanguages[0]
        if (language) this.viewMode = this.getTranslationViewValue(language)
        await this.loadPageTranslations()
      } catch (err) {
        this.setSnackbar({ open: true, text: this.getErrorMessage(err, 'Failed to generate category translations'), type: 'error' })
      } finally {
        this.isLoading.generateTranslations = false
      }
    },
    getErrorMessage (error, defaultMessage) {
      if (error?.response?.data?.message) return error.response.data.message
      if (error?.response?.data?.error) return error.response.data.error
      if (error?.message) return error.message
      if (typeof error === 'string') return error
      return defaultMessage
    }
  },
  components: {
    SnackbarAction,
    CategoryContentReviewDialog
  }
}
